import React, { FC, useMemo, useState, useRef } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { Icon, Label, TextInputHelper, Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import {
    ContentFile,
    PublicationForm,
    PublicationType,
    StisPublicationAction,
    visibilityTypeOptions,
} from '@web/vin-sessions/types';
import { createUseStyles } from 'react-jss';
import { State, UsePublicationFormReturnType } from './hooks/use-publication-form';
import { prepareCreatePublicationBody } from './autosave.utils';
import { getSaveErrorMessage } from './save-error.utils';
import { UploadPhase } from './hooks/use-upload-status';
import { Typeahead } from 'react-bootstrap-typeahead';
import colors from '@packages/core/styles/colors';
import { FileUpload } from '../file-upload';
import DatePicker from 'react-multi-date-picker';
import { useParams } from 'react-router-dom';
import { useAuthState } from '@packages/contexts/auth';
import { getLogger } from '@packages/core/logging';
import { docsService } from '@web/services/singletons';
import { publicationsActions } from './context/publications.state';
import { usePublicationsDispatch } from './context/publications.hooks';
import { StisFilterDropdown } from './stis-filter-dropdown';
import { toUploadFileType } from './content-file.utils';
import { parseVersion } from './version.utils';
import { TransferProgress, isAbortError, isSuccessStatus } from '@web/vin-sessions/utils/upload-transfer';
import {
    PUBLISHED_MONTHS,
    formatPublishedMonth,
    isServiceDiagnosticsLabel,
    isTechTipsLabel,
    publishedYearOptions,
} from './vehicle-filter.utils';

const logger = getLogger('vin-sessions');

const useStyles = createUseStyles({
    clearIcon: {
        position: 'absolute',
        right: 8,
        top: 8,
        zIndex: 2,
        backgroundColor: colors.white,
        cursor: 'pointer',
    },
    dropdown: {
        '& .dropdown-menu': {
            width: 280,
            maxHeight: 300,
            padding: '8px 0 !important',
            borderRadius: `3px !important`,
            border: `1px solid ${colors.grayThree} !important`,
            boxShadow: `0px 16px 12px -12px rgba(0, 0, 0, 0.16) !important`,
            overflowY: 'auto !important',
        },
        '& .dropdown-menu::-webkit-scrollbar': {
            width: 10,
        },
        '& .dropdown-menu::-webkit-scrollbar-track': {
            backgroundColor: '#ffffff',
        },
        '& .dropdown-menu::-webkit-scrollbar-thumb': {
            backgroundColor: colors.blueFour,
            borderRadius: 10,
            border: '2px solid #ffffff',
        },
    },
});

interface Props {
    publicationInfo: PublicationForm;
    actions?: UsePublicationFormReturnType['actions'];
    publicationTypes: PublicationType[];
    state: State;
    action: StisPublicationAction;
    fileUploadStatus: boolean;
    setFileUploadStatus: React.Dispatch<React.SetStateAction<boolean>>;
    onFileSent: (upload: { isZip: boolean; contentFile: ContentFile }) => void;
    onSendStart: () => AbortSignal;
    onSendProgress: (progress: TransferProgress) => void;
    sendProgress?: TransferProgress;
    fileAction?: { label: string; onOpen: () => void };
    uploadPhase: UploadPhase;
    lastCheckedAt?: Date;
    onCheckAgain: () => void;
}

export const DocumentInfo = ({
    publicationInfo,
    actions,
    publicationTypes,
    state,
    action,
    fileUploadStatus,
    setFileUploadStatus,
    onFileSent,
    onSendStart,
    onSendProgress,
    sendProgress,
    fileAction,
    uploadPhase,
    lastCheckedAt,
    onCheckAgain,
}: Props) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const datePickerRef = useRef<any>(null);
    const typeaheadRef = useRef<any>(null);
    const typeaheadRef1 = useRef<any>(null);
    const [closeIconOpen, setCloseIconOpen] = useState(false);
    const [closeIconOpen1, setCloseIconOpen1] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const { publicationFormId } = useParams<{ publicationFormId: string }>();
    const publicationDispatch = usePublicationsDispatch();
    const { account } = useAuthState();
    const isTechTips = isTechTipsLabel(publicationInfo?.publicationType?.label);
    const isServiceDiagnostics = isServiceDiagnosticsLabel(publicationInfo?.publicationType?.label);
    const isInteractive = publicationInfo.interactiveInd ?? true;
    const [versionText, setVersionText] = useState(String(publicationInfo.pubVersion ?? ''));
    const [shownVersion, setShownVersion] = useState(publicationInfo.pubVersion);
    if (publicationInfo.pubVersion !== shownVersion) {
        setShownVersion(publicationInfo.pubVersion);
        if ((parseVersion(versionText) ?? 0) !== publicationInfo.pubVersion) {
            setVersionText(String(publicationInfo.pubVersion ?? ''));
        }
    }
    const isVersionValid = (parseVersion(versionText) ?? 0) > 0;
    const handleVersionChange = (text: string) => {
        const digits = text.replace(/\D/g, '');
        setVersionText(digits);
        actions?.handlePubVersionChange(parseVersion(digits) ?? 0);
    };
    const yearOptions = useMemo(() => publishedYearOptions().map((year) => ({ value: year, label: year })), []);
    const monthOptions = useMemo(
        () => PUBLISHED_MONTHS.map((month) => ({ value: month, label: formatPublishedMonth(month) })),
        []
    );

    const handleClearSelection = (typeaheadRef: React.RefObject<any>) => {
        if (typeaheadRef.current) {
            typeaheadRef.current.clear();
            typeaheadRef.current.toggleMenu();
            typeaheadRef.current.inputNode.focus();
            return;
        }
    };

    const handleToggleMenu = (typeaheadRef: React.RefObject<any>) => {
        if (typeaheadRef.current) {
            typeaheadRef.current.toggleMenu();
            typeaheadRef.current.inputNode.focus();
        }
    };

    return (
        <>
            <Row className="mt-5">
                <Col xs={6} className="mb-3">
                    <div className="mb-2">
                        <Label required>{t('stisPublications:documentInfo.headers.title', 'Title')}</Label>
                    </div>
                    <TextInputHelper
                        required
                        value={publicationInfo.title != 'Untitled' ? publicationInfo.title : ''}
                        onChangeText={(title) => actions?.handleTitleChange(title)}
                        placeholder={t('stisPublications:documentInfo.placeholder.title', 'Write Publication Title')}
                        errorMessage={state.titleErrorMessage}
                        editable={action !== StisPublicationAction.AddVersion}
                    />
                </Col>

                <Col xs={6} className="mb-3">
                    <div className="mb-2">
                        <Label required>
                            {t('stisPublications:documentInfo.headers.publicationType', 'Choose Publication Type')}
                        </Label>
                    </div>
                    <Typeahead
                        placeholder={t(
                            'stisPublications:documentInfo.placeholder.publicationType',
                            'Choose Publication Type'
                        )}
                        labelKey="label"
                        selected={publicationInfo?.publicationType ? [publicationInfo?.publicationType] : []}
                        onChange={(publicationType) =>
                            actions?.handlePublicationTypeChange(publicationType[0], publicationInfo?.contentFile)
                        }
                        onInputChange={(value) => {
                            if (value.length == 0) setCloseIconOpen(false);
                            else setCloseIconOpen(true);
                        }}
                        options={publicationTypes}
                        ref={typeaheadRef}
                        className={classes.dropdown}
                        inputProps={{
                            style: { border: state.publicationTypeErrorMessage ? `2px solid ${colors.redOne}` : '' },
                        }}
                    >
                        <div className={classes.clearIcon}>
                            {publicationInfo?.publicationType?.label != '' || closeIconOpen ? (
                                <Icon
                                    name="x-close"
                                    onPress={() => {
                                        handleClearSelection(typeaheadRef);
                                        actions?.handlePublicationTypeChange(
                                            {
                                                label: '',
                                                code: '',
                                                description: '',
                                                id: '',
                                            },
                                            publicationInfo?.contentFile
                                        );
                                        setCloseIconOpen(false);
                                    }}
                                />
                            ) : (
                                <Icon
                                    name="chevron-down"
                                    onPress={() => handleToggleMenu(typeaheadRef)}
                                    color={state.publicationTypeErrorMessage ? 'redOne' : 'textDarkTertiary'}
                                />
                            )}
                        </div>
                    </Typeahead>
                    {state.publicationTypeErrorMessage && (
                        <Typography variant="caption" color="redOne">
                            {state.publicationTypeErrorMessage}
                        </Typography>
                    )}
                </Col>
            </Row>

            <Row className="mt-5">
                <Col xs={6} className="mb-3">
                    <div className="mb-2">
                        <Label required>{t('stisPublications:documentInfo.headers.docCode', 'Document Code')}</Label>
                    </div>
                    <TextInputHelper
                        required
                        value={publicationInfo.docCode === null ? '' : publicationInfo.docCode}
                        onChangeText={(docCode) => actions?.handleDocumentCodeChange(docCode)}
                        placeholder={t('stisPublications:documentInfo.placeholder.docCode', 'Write a code')}
                        errorMessage={state.docCodeErrorMessage}
                    />
                </Col>

                <Col xs={6} className="mb-3">
                    <div className="mb-2">
                        <Label required>
                            {t('stisPublications:documentInfo.headers.visibilityType', 'Visibility Type"')}
                        </Label>
                    </div>
                    <Typeahead<any>
                        id="visibilityType"
                        placeholder={t(
                            'stisPublications:documentInfo.placeholder.visibilityType',
                            'Choose Visibility Type'
                        )}
                        labelKey="label"
                        selected={publicationInfo.visibilityLevel ? [publicationInfo.visibilityLevel] : []}
                        onChange={(visibilityType) => actions?.handleVisibilityTypeChange(visibilityType[0])}
                        onInputChange={(value) => {
                            if (value.length == 0) setCloseIconOpen1(false);
                            else setCloseIconOpen1(true);
                        }}
                        options={visibilityTypeOptions}
                        ref={typeaheadRef1}
                        className={classes.dropdown}
                        inputProps={{
                            style: { border: state.visibilityErrorMessage ? `2px solid ${colors.redOne}` : '' },
                        }}
                    >
                        <div className={classes.clearIcon}>
                            {(publicationInfo.visibilityLevel?.label || closeIconOpen1) != '' ? (
                                <Icon
                                    name="x-close"
                                    onPress={() => {
                                        handleClearSelection(typeaheadRef1);
                                        actions?.handleVisibilityTypeChange({ label: '', value: '' });
                                        setCloseIconOpen1(false);
                                    }}
                                />
                            ) : (
                                <Icon
                                    name="chevron-down"
                                    onPress={() => handleToggleMenu(typeaheadRef1)}
                                    color={state.visibilityErrorMessage ? 'redOne' : 'textDarkTertiary'}
                                />
                            )}
                        </div>
                    </Typeahead>
                    {state.visibilityErrorMessage && (
                        <Typography variant="caption" color="redOne">
                            {state.visibilityErrorMessage}
                        </Typography>
                    )}
                </Col>
            </Row>

            <Row className="mt-5">
                <Col xs={6} className="mb-3">
                    <div className="mb-2">
                        <Label required>{t('stisPublications:documentInfo.headers.version', 'Version')}</Label>
                    </div>
                    <TextInputHelper
                        required
                        value={versionText}
                        onChangeText={handleVersionChange}
                        placeholder={t('stisPublications:documentInfo.placeholder.version', 'Enter a version')}
                        errorMessage={
                            isVersionValid
                                ? undefined
                                : t('stisPublications:validationMessage.version', 'Version must be a number above 0')
                        }
                    />
                </Col>

                {isServiceDiagnostics && (
                    <Col xs={6} className="mb-3">
                        <div className="mb-2">
                            <Label required>
                                {t(
                                    'stisPublications:documentInfo.headers.interactive',
                                    'Turn on Interactive Diagnostics?'
                                )}
                            </Label>
                        </div>
                        <Form.Check
                            inline
                            type="radio"
                            id="interactive-yes"
                            name="interactiveInd"
                            label={t('stisPublications:documentInfo.interactiveOptions.yes', 'Yes')}
                            checked={isInteractive}
                            onChange={() => actions?.handleInteractiveIndChange(true)}
                        />
                        <Form.Check
                            inline
                            type="radio"
                            id="interactive-no"
                            name="interactiveInd"
                            label={t('stisPublications:documentInfo.interactiveOptions.no', 'No')}
                            checked={!isInteractive}
                            onChange={() => actions?.handleInteractiveIndChange(false)}
                        />
                    </Col>
                )}
            </Row>

            {isTechTips && (
                <Row className="mt-5">
                    <Col xs={6} className="mb-3">
                        <div className="mb-2">
                            <Label required>
                                {t('stisPublications:documentInfo.headers.publishedMonth', 'Published Month')}
                            </Label>
                        </div>
                        <StisFilterDropdown
                            ariaLabel={t('stisPublications:documentInfo.headers.publishedMonth', 'Published Month')}
                            placeholder={t('stisPublications:documentInfo.placeholder.publishedMonth', 'Choose month')}
                            options={monthOptions}
                            selected={publicationInfo.publishedMonth ? [String(publicationInfo.publishedMonth)] : []}
                            width="100%"
                            onChange={(values) => values[0] && actions?.handlePublishedMonthChange(Number(values[0]))}
                        />
                        {state.publishedMonthErrorMessage && (
                            <Typography variant="caption" color="redOne">
                                {state.publishedMonthErrorMessage}
                            </Typography>
                        )}
                    </Col>

                    <Col xs={6} className="mb-3">
                        <div className="mb-2">
                            <Label required>
                                {t('stisPublications:documentInfo.headers.publishedYear', 'Published Year')}
                            </Label>
                        </div>
                        <StisFilterDropdown
                            ariaLabel={t('stisPublications:documentInfo.headers.publishedYear', 'Published Year')}
                            placeholder={t('stisPublications:documentInfo.placeholder.publishedYear', 'Choose year')}
                            options={yearOptions}
                            selected={publicationInfo.publishedYear ? [String(publicationInfo.publishedYear)] : []}
                            width="100%"
                            onChange={(values) => values[0] && actions?.handlePublishedYearChange(Number(values[0]))}
                        />
                        {state.publishedYearErrorMessage && (
                            <Typography variant="caption" color="redOne">
                                {state.publishedYearErrorMessage}
                            </Typography>
                        )}
                    </Col>
                </Row>
            )}

            {/* need to make url validation*/}
            {/* <Row className="mt-5">
                <Col xs={12} className="mb-3">
                    <div className="mb-2">
                        <Label required>{t('stisPublications:documentInfo.headers.contentLink', 'Content Link')}</Label>
                    </div>
                    <TextInputHelper
                        required
                        value={publicationInfo.url}
                        onChangeText={(url) => actions?.handleContentURLChange(url)}
                        placeholder={t('stisPublications:documentInfo.placeholder.contentLink', 'Enter URL')}
                        errorMessage={state.contentLinkErrorMessage}
                    />
                </Col>
            </Row> */}

            <Row className="mt-5">
                <Col xs={12} className="mb-3">
                    <FileUpload
                        label={t('stisPublications:documentInfo.headers.contentFile', 'Content File')}
                        iconName="export"
                        placeholder1={t(
                            'stisPublications:documentInfo.placeholder.fileUploadPlaceholder1',
                            'Click to upload or drag and drop'
                        )}
                        placeholder2={
                            isServiceDiagnostics
                                ? t(
                                      'stisPublications:documentInfo.placeholder.fileUploadPlaceholderSD',
                                      'PDF, DOC, DOCX, ZIP'
                                  )
                                : t(
                                      'stisPublications:documentInfo.placeholder.fileUploadPlaceholder2',
                                      'PDF, DOC, DOCX'
                                  )
                        }
                        value={selectedFile}
                        fileUploadStatus={fileUploadStatus}
                        sendProgress={sendProgress}
                        fileAction={fileAction}
                        isServiceDiagnostics={isServiceDiagnostics}
                        uploadPhase={uploadPhase}
                        lastCheckedAt={lastCheckedAt}
                        onCheckAgain={onCheckAgain}
                        onChange={async (file) => {
                            setSelectedFile(file);
                            if (file) {
                                const body = {
                                    documentId: publicationFormId,
                                    fileName: file.name,
                                    fileType: toUploadFileType(file),
                                    createdBy: account?.accountId || '',
                                    sizeInBytes: file.size,
                                    createdAt: new Date().toISOString(),
                                };
                                const isZip = isServiceDiagnostics && body.fileType === 'ZIP';
                                const contentFile: ContentFile = {
                                    fileName: file.name,
                                    fileType: body.fileType,
                                    createdDate: new Date().toISOString(),
                                    createdTimestamp: new Date().toISOString(),
                                    createdBy: `${account?.firstName ?? ''} ${account?.lastName ?? ''}`.trim(),
                                };

                                if (isZip && !isVersionValid) {
                                    setSelectedFile(null);
                                    publicationDispatch(
                                        publicationsActions.setToastMessage({
                                            toast: {
                                                toastMessage: t(
                                                    'stisPublications:validationMessage.version',
                                                    'Version must be a number above 0'
                                                ),
                                                iconColor: 'white',
                                                iconName: 'x-close',
                                            },
                                        })
                                    );
                                    return;
                                }

                                // Only the send happens here; the form polls for processing
                                const signal = onSendStart();
                                try {
                                    setFileUploadStatus(true);

                                    if (isZip) {
                                        await docsService.editPublication({
                                            publicationId: publicationFormId || '',
                                            body: prepareCreatePublicationBody(publicationInfo),
                                        });
                                        const response = await docsService.sendZipDocument({
                                            documentId: publicationFormId || '',
                                            file,
                                            onProgress: onSendProgress,
                                            signal,
                                        });
                                        if (!isSuccessStatus(response.status)) {
                                            throw new Error(
                                                getSaveErrorMessage(
                                                    response.body,
                                                    'Error in uploading the file, please try again'
                                                )
                                            );
                                        }
                                    } else {
                                        const response = await docsService.uploadDocument({ body });
                                        const preSignedUploadUrl = response.success
                                            ? response.data?.preSignedUploadUrl
                                            : undefined;
                                        if (!preSignedUploadUrl) {
                                            throw new Error(
                                                getSaveErrorMessage(
                                                    response.data,
                                                    'Error in uploading the file, please try again'
                                                )
                                            );
                                        }
                                        await docsService.uploadFileToS3({
                                            file,
                                            preSignedUploadUrl,
                                            onProgress: onSendProgress,
                                            signal,
                                        });
                                    }

                                    setFileUploadStatus(false);
                                    onFileSent({ isZip, contentFile });
                                } catch (error) {
                                    if (isAbortError(error)) {
                                        setFileUploadStatus(false);
                                        return;
                                    }
                                    logger.error(
                                        `Failed to upload the file for the publication [${publicationFormId}]`,
                                        {
                                            error,
                                            publicationFormId,
                                        }
                                    );
                                    setFileUploadStatus(false);
                                    publicationDispatch(
                                        publicationsActions.setToastMessage({
                                            toast: {
                                                toastMessage:
                                                    (error as { message?: string })?.message ||
                                                    'Error in uploading the file, please try again',
                                                iconColor: 'white',
                                                iconName: 'x-close',
                                            },
                                        })
                                    );
                                }
                            }
                        }}
                        disabled={
                            !publicationInfo?.publicationType?.id ||
                            publicationInfo.title === 'Untitled' ||
                            !publicationFormId
                        }
                        publicationInfo={publicationInfo}
                        deleteAction={async () => {
                            actions?.handleContentFileChange({
                                fileName: '',
                                fileType: '',
                                createdDate: '',
                                createdTimestamp: '',
                                createdBy: '',
                            });
                            await docsService.deleteDocument({ documentId: publicationFormId || '' });
                            actions?.handleFileUploadFlag(true);
                            setSelectedFile(null);
                        }}
                        errorMessage={state.contentFileErrorMessage}
                    />
                </Col>
            </Row>

            <Row className="mt-5">
                <style>
                    {`
                        .rmdp-container {
                            width: 100% !important;
                        }
                    `}
                </style>
                <Col xs={6} className="mb-3">
                    <div className="mb-2">
                        <Label>{t('stisPublications:documentInfo.headers.created', 'Created')}</Label>
                    </div>
                    <DatePicker
                        value={publicationInfo.createdDate}
                        style={{ width: '100% !important' }}
                        calendarPosition="right"
                        ref={datePickerRef}
                        render={
                            <div onClick={() => datePickerRef.current && datePickerRef.current.openCalendar()}>
                                <TextInputHelper
                                    style={{ cursor: 'pointer' }}
                                    rightIcon={
                                        <Icon
                                            name="calender-month"
                                            onPress={() =>
                                                datePickerRef.current && datePickerRef.current.openCalendar()
                                            }
                                        />
                                    }
                                    value={
                                        publicationInfo.createdDate
                                            ? new Date(publicationInfo.createdDate).toLocaleDateString()
                                            : ''
                                    }
                                />
                            </div>
                        }
                        onChange={(date) => actions?.handleCreatedDateChange(date ? date.toDate() : null)}
                    />
                </Col>
            </Row>
        </>
    );
};
